import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ProcessActionModel, ProcessStepModel } from '../../process.model';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../app.service';
import { selectStepById } from '../../selectors/process.selectors';
import * as Defaults from '../../default-process-fields';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { AddQuestionComponent } from '../../../../questions-module/components/add-question.component';
import { ModalService } from '../../../../lib/services/modal.service';
import { UpdateJobProcessStep } from '../../actions/process.actions';
import { ProcessQuestionModel } from '../../../../questions-module/models/questions.model';
import * as fromJobs from '../../../reducers';
import * as jobActions from '../../../actions/jobs.actions';
import { Subscription } from 'rxjs';

@Component({
  selector: 'mj-process-one-way-video',
  templateUrl: './process-one-way-video.component.html',
  styleUrls: ['./process-one-way-video.component.scss'],
})
export class ProcessOneWayVideoComponent implements OnInit, OnDestroy {
  @Input() stepOpenedUuid$;
  defaults = Defaults;
  @Output() blurChanged = new EventEmitter<boolean>();
  @Output() processChanged = new EventEmitter();
  step: ProcessStepModel;
  questions: ProcessQuestionModel[];
  @Input() stepId: number;
  isStepActive: boolean;

  questionsBackup = [];
  daysMiliseconds = [];

  messageAction: ProcessActionModel;
  reminderAction: ProcessActionModel;

  isDefaultMessage;
  private step$: Subscription;

  constructor(
    private store: Store<AppState>,
    private jobStore: Store<fromJobs.State>,
    private modalService: ModalService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.prepareDays();
    this.step$ = this.store
      .pipe(select(selectStepById(this.stepId)))
      .subscribe(data => {
        if (data) {
          // checking if user removed all questions if so - toggle and clear backup
          if (
            this.step &&
            !data.questions.length &&
            this.step.questions.length &&
            this.step.enabled &&
            data.enabled
          ) {
            this.processChanged.emit({
              id: this.stepId,
              changes: { enabled: false, questions: [] },
            });
            this.questionsBackup = [];
          }

          if (
            this.step &&
            this.step.enabled !== data.enabled &&
            this.isDefaultMessage
          ) {
            this.setDefaultMessage(data);
          }

          this.step = data;
          this.questions = JSON.parse(JSON.stringify(data.questions));

          this.jobStore.dispatch(
            new jobActions.SetOneWayVideoInterview({
              hasVideoInterview: this.questions.length > 0,
            })
          );
          this.messageAction = this.step.actions.find(
            action => action.type === 0
          );
          this.reminderAction = this.step.actions.find(
            action => action.type === 1
          );

          if (this.messageAction && !this.messageAction.message) {
            this.setDefaultMessage();
          }

          this.setDefaultActions();

          if (
            !this.step.enabled &&
            this.reminderAction &&
            this.reminderAction.enabled
          ) {
            this.updateAction(this.reminderAction, {
              enabled: false,
            });
          }
        }
      });

    this.stepOpenedUuid$.subscribe(uuid => {
      this.isStepActive = uuid === this.step.uuid;

      if (this.step.enabled && !this.step.questions.length) {
        this.processChanged.emit({
          id: this.stepId,
          changes: { enabled: false },
        });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.step$) {
      this.step$.unsubscribe();
    }
  }

  toggleEnabled(e) {
    e.stopPropagation();
    this.processChanged.emit({
      id: this.stepId,
      changes: { enabled: !this.step.enabled },
    });
  }

  updateAction(action, changes: {}) {
    const newAction = { ...action };
    const key = Object.keys(changes)[0];
    newAction[key] = changes[key];

    const newArr = this.step.actions.map(elem => {
      if (elem.type === newAction.type) {
        return newAction;
      } else {
        return elem;
      }
    });

    if (changes['message']) {
      this.isDefaultMessage = false;
    }

    this.processChanged.emit({
      id: this.stepId,
      changes: { actions: newArr },
    });
  }

  onAddQuestionClicked() {
    this.blurChanged.emit(true);
    this.modalService
      .appendComponentToBody(AddQuestionComponent, Object.assign({}, this.step))
      .pipe(take(1))
      .subscribe(data => {
        if (data) {
          this.store.dispatch(
            new UpdateJobProcessStep({
              id: this.stepId,
              changes: {
                questions: data,
              },
            })
          );
        }
        this.blurChanged.emit(false);
      });
  }

  setTimeToReminder(e) {
    this.updateAction(this.reminderAction, {
      millis_after_entering: e.value,
    });
  }

  toggleQuestions() {
    const toggleStatus = !this.step.enabled;

    let questions = [];
    if (!toggleStatus) {
      this.updateAction(this.reminderAction, { enabled: false });
      this.questionsBackup = [...this.step.questions];
    } else {
      questions = this.questionsBackup;
      this.onAddQuestionClicked();
    }

    this.processChanged.emit({
      id: this.stepId,
      changes: { enabled: toggleStatus, questions },
    });
    this.setDefaultMessage();
  }

  setDefaultActions() {
    const newActions: any = [...this.step.actions];
    let shouldUpdate = false;
    if (!this.reminderAction) {
      const newAction = {
        ...this.defaults.DefaultActions[1],
        message: this.translate.instant(
          'step_video_interview_reminder_message'
        ),
      };
      newActions.push(newAction);
      shouldUpdate = true;
    }

    if (!this.messageAction) {
      newActions.push(this.defaults.DefaultActions[0]);
      shouldUpdate = true;
    }

    if (shouldUpdate) {
      this.processChanged.emit({
        id: this.stepId,
        changes: { actions: newActions },
      });
    }
  }

  setDefaultMessage(data?) {
    const step = data ? data : this.step;

    this.translate
      .get(
        step.enabled ? 'step_video_interview_message' : 'step_qualified_message'
      )
      .pipe(take(1))
      .subscribe(msg => {
        const newAction = Object.assign({}, this.messageAction, {
          message: msg,
        });
        const newArr = [...this.step.actions].map(elem => {
          if (elem.type === newAction.type) {
            return newAction;
          } else {
            return elem;
          }
        });
        this.processChanged.emit({
          id: this.stepId,
          changes: { actions: newArr },
        });

        this.isDefaultMessage = true;
      });
  }

  prepareDays() {
    for (let i = 1; i <= 31; i++) {
      this.daysMiliseconds.push(86400000 * i);
    }
  }
}
